import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useAdminStats, useAdminPhotos, useAdminSettings, useAdminAuditLog, useAdminActions } from '../hooks/useAdmin'
import { formatFileSize, formatDate, classNames } from '../utils/helpers'

type Tab = 'overview' | 'photos' | 'settings' | 'password' | 'danger'

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('overview')
  const { stats, isLoading: statsLoading, load: loadStats } = useAdminStats()
  const { photos, pagination, isLoading: photosLoading, load: loadPhotos, deletePhoto, deleteMultiple } = useAdminPhotos()
  const { settings, isLoading: settingsLoading, isSaving, load: loadSettings, save: saveSettings } = useAdminSettings()
  const { logs, load: loadLogs } = useAdminAuditLog()
  const { isLoading: actionLoading, changePassword, cleanup, deleteAll } = useAdminActions()

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [form, setForm] = useState<Record<string, string>>({})
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const [curPwd, setCurPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [isAdminPwd, setIsAdminPwd] = useState(true)
  const [deleteConfirm, setDeleteConfirm] = useState('')

  useEffect(() => { loadStats(); loadSettings(); loadLogs() }, [loadStats, loadSettings, loadLogs])
  useEffect(() => { if (tab === 'photos') loadPhotos(1) }, [tab, loadPhotos])
  useEffect(() => {
    if (settings) {
      setForm({
        expiry_date: String(settings.expiry_date ?? '2026-12-31'),
        maintenance_mode: String(settings.maintenance_mode),
        maintenance_message: String(settings.maintenance_message ?? ''),
        max_file_size_mb: String(settings.max_file_size_mb ?? 20),
        max_files_per_upload: String(settings.max_files_per_upload ?? 100),
        max_total_upload_size_mb: String(settings.max_total_upload_size_mb ?? 500),
        allow_uploads: String(settings.allow_uploads),
        gallery_title: String(settings.gallery_title ?? ''),
        gallery_subtitle: String(settings.gallery_subtitle ?? ''),
        album_name: String(settings.album_name ?? ''),
        privacy_notice: String(settings.privacy_notice ?? ''),
      })
    }
  }, [settings])

  const toggle = (id: number) => setSelected(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n
  })

  const handleSave = async () => {
    setMsg(null); setErr(null)
    try {
      await saveSettings({
        expiry_date: form.expiry_date,
        maintenance_mode: form.maintenance_mode === 'true',
        maintenance_message: form.maintenance_message,
        max_file_size_mb: Number(form.max_file_size_mb),
        max_files_per_upload: Number(form.max_files_per_upload),
        max_total_upload_size_mb: Number(form.max_total_upload_size_mb),
        allow_uploads: form.allow_uploads === 'true',
        gallery_title: form.gallery_title,
        gallery_subtitle: form.gallery_subtitle,
        album_name: form.album_name,
        privacy_notice: form.privacy_notice,
      } as never)
      setMsg('Einstellungen gespeichert.')
      loadStats()
    } catch (e) { setErr(e instanceof Error ? e.message : 'Speichern fehlgeschlagen') }
  }

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return
    if (!confirm(`${selected.size} Fotos wirklich löschen?`)) return
    try { await deleteMultiple([...selected]); setSelected(new Set()); setMsg(`${selected.size} Fotos gelöscht.`); loadStats() }
    catch (e) { setErr(e instanceof Error ? e.message : 'Löschen fehlgeschlagen') }
  }

  const handlePassword = async () => {
    setMsg(null); setErr(null)
    try {
      const r = await changePassword(curPwd, newPwd, isAdminPwd)
      setMsg('Neuer Hash erzeugt. Trage ihn als Secret ein: ' + r.newHash.slice(0, 24) + '...')
      alert('WICHTIG — neuer Hash (kopieren und als Secret speichern):\n\n' + r.newHash + '\n\n' + r.message)
      setCurPwd(''); setNewPwd('')
    } catch (e) { setErr(e instanceof Error ? e.message : 'Fehler') }
  }

  return (
    <Layout>
      <div className="container admin-page">
        <div className="admin-header">
          <div>
            <h1>Administration</h1>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Nur für den Veranstalter. Alle Aktionen werden protokolliert.</p>
          </div>
          <Link to="/gallery" className="btn btn-secondary btn-sm">Zurück zur Galerie</Link>
        </div>

        {(msg || err) && (
          <div className={classNames('privacy-notice')} role={err ? 'alert' : 'status'} style={err ? { borderColor: 'rgba(255,107,107,.4)', color: 'var(--color-danger)' } : undefined}>
            {err ?? msg}
          </div>
        )}

        <div className="admin-tabs" role="tablist">
          {([['overview', 'Übersicht'], ['photos', 'Fotos'], ['settings', 'Einstellungen'], ['password', 'Passwort'], ['danger', 'Löschen & Wartung']] as [Tab, string][]).map(([t, label]) => (
            <button key={t} role="tab" aria-selected={tab === t} className={classNames('admin-tab', { 'admin-tab--active': tab === t })} onClick={() => setTab(t)}>{label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <div className="admin-stats-grid">
              <div className="admin-stat-card"><div className="admin-stat-value">{statsLoading ? '…' : stats?.photos ?? 0}</div><div className="admin-stat-label">Fotos gespeichert</div></div>
              <div className="admin-stat-card"><div className="admin-stat-value">{statsLoading ? '…' : formatFileSize(stats?.storageUsed ?? 0)}</div><div className="admin-stat-label">Speicher (D1-Summe)</div></div>
              <div className="admin-stat-card"><div className="admin-stat-value">{statsLoading ? '…' : formatFileSize(stats?.r2StorageUsed ?? 0)}</div><div className="admin-stat-label">R2-Speicher (Objekte: {stats?.r2Objects ?? 0})</div></div>
              <div className="admin-stat-card"><div className="admin-stat-value">{statsLoading ? '…' : stats?.expiry.daysRemaining ?? '–'}</div><div className="admin-stat-label">Tage bis {stats?.expiry.date}</div></div>
            </div>
            <div className="admin-section">
              <h2>Free-Tier Hinweise</h2>
              <ul style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', paddingLeft: 18, display: 'grid', gap: 6 }}>
                <li>R2 Free: 10 GB Speicher, 1 Mio. Class-A / 10 Mio. Class-B Ops pro Monat, Egress frei.</li>
                <li>D1 Free: 5 GB Speicher, 5 Mio. Reads/Tag, 100k Writes/Tag.</li>
                <li>Workers Free: 100k Requests/Tag, 10 ms CPU.</li>
                <li>600 Fotos à ~5 MB ≈ 3 GB — passt ins Free-Kontingent. Kosten im Cloudflare-Dashboard prüfen.</li>
              </ul>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => loadStats()}>Aktualisieren</button>
                <button className="btn btn-secondary btn-sm" disabled={actionLoading} onClick={async () => { const r = await cleanup(); setMsg(`Bereinigt: ${r.expiredSessions} Sessions, ${r.expiredUploads} Uploads.`); }}>Sessions bereinigen</button>
              </div>
            </div>
            <div className="admin-section">
              <h2>Letzte Admin-Aktionen</h2>
              <div className="audit-log">
                {logs.slice(0, 10).map(l => (
                  <div key={l.id} className="audit-entry"><span>{l.action} {l.targetType ? `· ${l.targetType}:${l.targetId}` : ''}</span><time>{formatDate(l.createdAt)}</time></div>
                ))}
                {logs.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>Noch keine Einträge.</p>}
              </div>
            </div>
          </>
        )}

        {tab === 'photos' && (
          <div className="admin-section">
            <h2>Fotos verwalten ({pagination.total})</h2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => loadPhotos(1)}>Aktualisieren</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set(photos.map(p => p.id)))}>Alle auswählen</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>Auswahl löschen</button>
              <button className="btn btn-danger btn-sm" disabled={selected.size === 0} onClick={handleDeleteSelected}>{selected.size} ausgewählte löschen</button>
            </div>
            {photosLoading ? <p>Lade...</p> : (
              <div className="admin-photo-list">
                {photos.map(p => (
                  <div key={p.id} className={classNames('admin-photo-item', { 'admin-photo-item--selected': selected.has(p.id) })}>
                    <img src={`/api/photos/${p.id}/thumbnail`} alt={p.filename} loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    <label className="admin-photo-check"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} aria-label={`Auswählen ${p.filename}`} /></label>
                    <button className="admin-photo-del" aria-label={`Löschen ${p.filename}`} onClick={async () => { if (confirm(`„${p.filename}" wirklich löschen?`)) { await deletePhoto(p.id); loadStats() } }}>×</button>
                    <div style={{ padding: 8, fontSize: 12 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.filename}</div><div style={{ color: 'var(--color-text-muted)' }}>{formatFileSize(p.fileSize)} · {formatDate(p.uploadedAt, { day: '2-digit', month: '2-digit', year: 'numeric' })}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div className="admin-section">
            <h2>Einstellungen</h2>
            {settingsLoading ? <p>Lade...</p> : (
              <>
                <div className="admin-form-grid">
                  <div className="admin-field"><label className="label" htmlFor="expiry">Gültig bis</label><input id="expiry" className="input" type="date" value={form.expiry_date || ''} onChange={e => setForm({ ...form, expiry_date: e.target.value })} /></div>
                  <div className="admin-field"><label className="label" htmlFor="album">Albumname</label><input id="album" className="input" value={form.album_name || ''} onChange={e => setForm({ ...form, album_name: e.target.value })} /></div>
                  <div className="admin-field"><label className="label" htmlFor="maxfile">Max MB pro Datei</label><input id="maxfile" className="input" type="number" min={1} max={1000} value={form.max_file_size_mb || ''} onChange={e => setForm({ ...form, max_file_size_mb: e.target.value })} /></div>
                  <div className="admin-field"><label className="label" htmlFor="maxcount">Max Dateien pro Upload</label><input id="maxcount" className="input" type="number" min={1} max={200} value={form.max_files_per_upload || ''} onChange={e => setForm({ ...form, max_files_per_upload: e.target.value })} /></div>
                  <div className="admin-field"><label className="label" htmlFor="maxtotal">Max Gesamt-MB pro Upload</label><input id="maxtotal" className="input" type="number" min={10} max={10000} value={form.max_total_upload_size_mb || ''} onChange={e => setForm({ ...form, max_total_upload_size_mb: e.target.value })} /></div>
                  <div className="admin-field"><label className="label" htmlFor="title">Titel</label><input id="title" className="input" value={form.gallery_title || ''} onChange={e => setForm({ ...form, gallery_title: e.target.value })} /></div>
                  <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label className="label" htmlFor="subtitle">Untertitel</label><input id="subtitle" className="input" value={form.gallery_subtitle || ''} onChange={e => setForm({ ...form, gallery_subtitle: e.target.value })} /></div>
                  <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label className="label" htmlFor="privacy">Datenschutzhinweis</label><textarea id="privacy" className="input" rows={3} value={form.privacy_notice || ''} onChange={e => setForm({ ...form, privacy_notice: e.target.value })} /></div>
                  <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label className="label" htmlFor="maintmsg">Wartungsnachricht</label><input id="maintmsg" className="input" value={form.maintenance_message || ''} onChange={e => setForm({ ...form, maintenance_message: e.target.value })} /></div>
                  <label className="admin-checkbox"><input type="checkbox" checked={form.allow_uploads === 'true'} onChange={e => setForm({ ...form, allow_uploads: String(e.target.checked) })} /> Uploads erlauben</label>
                  <label className="admin-checkbox"><input type="checkbox" checked={form.maintenance_mode === 'true'} onChange={e => setForm({ ...form, maintenance_mode: String(e.target.checked) })} /> Wartungsmodus aktiv</label>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" disabled={isSaving} onClick={handleSave}>{isSaving ? 'Speichert...' : 'Speichern'}</button>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'password' && (
          <div className="admin-section">
            <h2>Passwort ändern</h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 12 }}>Aus Sicherheit erzeugt der Server nur einen neuen Hash. Du musst ihn danach als Secret (<code>PASSWORD_HASH</code> bzw. <code>ADMIN_PASSWORD_HASH</code>) speichern und neu deployen. Anleitung in README.</p>
            <div className="admin-form-grid">
              <div className="admin-field"><label className="label">Aktuelles Passwort</label><input className="input" type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)} /></div>
              <div className="admin-field"><label className="label">Neues Passwort (min. 8 Zeichen)</label><input className="input" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} /></div>
              <label className="admin-checkbox"><input type="checkbox" checked={isAdminPwd} onChange={e => setIsAdminPwd(e.target.checked)} /> Admin-Passwort ändern (sonst Galerie-Passwort)</label>
            </div>
            <div style={{ marginTop: 12 }}><button className="btn btn-primary" disabled={actionLoading || !curPwd || newPwd.length < 8} onClick={handlePassword}>Neuen Hash erzeugen</button></div>
          </div>
        )}

        {tab === 'danger' && (
          <div className="admin-section danger-zone">
            <h2>Gefahrenzone</h2>
            <p style={{ fontSize: 'var(--font-size-sm)', marginBottom: 12 }}>Es erfolgt <strong>keine</strong> automatische Löschung nach Fristablauf. Die Galerie wechselt in Nur-Lesen. Nur hier kannst du alles endgültig löschen.</p>
            <div className="admin-field" style={{ maxWidth: 420 }}><label className="label">Zur Bestätigung exakt eingeben: ALLE FOTOS ENDGÜLTIG LÖSCHEN</label><input className="input" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="Bestätigungstext" /></div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-danger" disabled={actionLoading || deleteConfirm !== 'ALLE FOTOS ENDGÜLTIG LÖSCHEN'} onClick={async () => { if (!confirm('Wirklich ALLE Fotos und Metadaten löschen? Nicht umkehrbar!')) return; const r = await deleteAll(deleteConfirm); setMsg(`${r.deleted} Fotos gelöscht.`); setDeleteConfirm(''); loadStats(); loadPhotos(1) }}>Alle Fotos endgültig löschen</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
